/**
 * The send form's role row ("who is signing?") resolves documents from the default templates: a
 * seller's or a landlord's order, plus the exclusivity order when "החתמה בבלעדיות" is ticked. A
 * template the session has not cached is FETCHED, and until it arrives the selection is still the
 * previous one: a ticked box over the order alone, or the last role's documents. "הכנת חוזה" sends
 * the selection, so it must wait. These pure rules say what the role sends, whether the selection is
 * exactly that, and if not, what the form tells the realtor (`RoleDocumentsNote`). They live outside
 * `NewSignatureForm`, whose body must not gain or lose a branch (react-hooks path counts).
 */
import {
  SIGNATURE_EXCLUSIVITY_TEMPLATE_KEYS,
  SIGNATURE_QUESTIONNAIRE_ONLY_ROLES,
  SIGNATURE_QUESTIONNAIRE_ONLY_TEMPLATE_KEY,
  signatureTemplateKeysForScenario
} from "../../../@types/sharedSchema";
import type { SignatureDefaultTemplateKey, SignatureDefaultTemplates } from "../../../@types/sharedSchema";
import type { SignatureTemplate } from "../../../@types/signatures";

/** The templates one pick of the role row sends, in send order. */
export type RoleTemplatePlan = {
  /** Template ids, without repeats: a role and its exclusivity may point at the same template. */
  ids: string[];
  /** The id the exclusivity slot adds, when it adds one. */
  exclusivityId: string | null;
  /** The pick is "שאלון בלבד": the one document is the owner questionnaire, not a contract. */
  questionnaire: boolean;
};

/** What `role` sends with the exclusivity box and "שאלון בלבד" as given (`signatureTemplateKeysForScenario`). */
export function roleTemplatePlan(
  role: SignatureDefaultTemplateKey,
  exclusive: boolean,
  questionnaire: boolean,
  defaults: SignatureDefaultTemplates
): RoleTemplatePlan {
  const questionnaireOnly = questionnaire && SIGNATURE_QUESTIONNAIRE_ONLY_ROLES.includes(role);
  const scenario = questionnaireOnly ? SIGNATURE_QUESTIONNAIRE_ONLY_TEMPLATE_KEY : role;
  const exclusivityKey = SIGNATURE_EXCLUSIVITY_TEMPLATE_KEYS[scenario];
  const ids: string[] = [];
  let exclusivityId: string | null = null;
  for (const key of signatureTemplateKeysForScenario(scenario, exclusive)) {
    const id = defaults[key];
    if (!id || ids.includes(id)) continue;
    ids.push(id);
    if (key === exclusivityKey) exclusivityId = id;
  }
  return { ids, exclusivityId, questionnaire: questionnaireOnly };
}

/**
 * Why the role's templates are not (all) selected: none is configured, or a fetch failed. A failure
 * belongs to the plan it was fetched for (`planIds`): another pick is a new resolution, never "failed".
 */
export type RoleIssue = { kind: "noDefault" } | { kind: "loadFailed"; planIds: string[]; failedIds: string[] };

/**
 * What the form names: the exclusivity order alone (the realtor may go on without it), the contract,
 * several contracts, or the owner questionnaire of "שאלון בלבד".
 */
export type RoleDocumentsSubject = "exclusivity" | "contract" | "contracts" | "questionnaire";

/** Why "הכנת חוזה" waits: a document the role sends is still loading, or could not be loaded. */
export type RoleDocumentsBlock = { state: "loading" | "failed"; subject: RoleDocumentsSubject };

export type RoleDocumentsState = {
  /** No template is configured for the role: the form offers the manual picker, as before. */
  noDefault: boolean;
  /** The documents the role row lists: what is selected of what the role sends, in send order. */
  listed: SignatureTemplate[];
  /** Set while the selection is not exactly what the role sends. */
  block: RoleDocumentsBlock | null;
};

/**
 * Compares what the role row sends with what is selected. With no role in charge (a manual pick, a
 * template or bundle link, an edit) or before the defaults load, the selection is what it is.
 */
export function roleDocumentsState(input: {
  role: SignatureDefaultTemplateKey | "custom" | null;
  exclusive: boolean;
  questionnaire: boolean;
  defaults: SignatureDefaultTemplates | null;
  selected: SignatureTemplate[];
  issue: RoleIssue | null;
}): RoleDocumentsState {
  const { role, defaults, selected, issue } = input;
  const noDefault = issue?.kind === "noDefault";
  if (!role || role === "custom" || !defaults || noDefault) return { noDefault, listed: selected, block: null };
  const plan = roleTemplatePlan(role, input.exclusive, input.questionnaire, defaults);
  // Nothing configured: the role effect is about to say so (noDefault).
  if (plan.ids.length === 0) return { noDefault, listed: selected, block: null };
  const selectedIds = selected.map((tp) => tp.id);
  const listed = plan.ids
    .map((id) => selected.find((tp) => tp.id === id))
    .filter((tp): tp is SignatureTemplate => !!tp);
  const exact = selectedIds.length === plan.ids.length && plan.ids.every((id) => selectedIds.includes(id));
  if (exact) return { noDefault, listed, block: null };
  const missing = plan.ids.filter((id) => !selectedIds.includes(id));
  const failedForThisPlan =
    issue?.kind === "loadFailed" &&
    issue.planIds.length === plan.ids.length &&
    plan.ids.every((id) => issue.planIds.includes(id));
  const failed = failedForThisPlan ? missing.filter((id) => issue.failedIds.includes(id)) : [];
  // A failure is named first; otherwise the selection is on its way to the plan (a fetch, or the
  // role effect that runs right after the render that changed the pick).
  const named = failed.length > 0 ? failed : missing;
  const subject: RoleDocumentsSubject =
    named.length === 1 && named[0] === plan.exclusivityId
      ? "exclusivity"
      : named.length > 1
      ? "contracts"
      : plan.questionnaire
      ? "questionnaire"
      : "contract";
  return { noDefault, listed, block: { state: failed.length > 0 ? "failed" : "loading", subject } };
}

/** The i18n key of the sentence that says why "הכנת חוזה" waits (the note's first line, and `validate()`'s answer). */
export function roleDocumentsBlockKey(block: RoleDocumentsBlock): string {
  return `signatures.newSignatureForm.roleDocuments.${block.state}.${block.subject}`;
}
